import React, { useMemo, useState, useEffect } from 'react'
import { InputNumber, Select, Tooltip } from 'antd'

const periodOptions = [
  { label: 'Annual', value: 'annual' },
  { label: 'Monthly', value: 'monthly' },
]

const to2 = (n) => {
  const num = Number(n) || 0
  return Math.round(num * 100) / 100
}

export default function PeriodInput({
  name,
  label,
  tooltip,
  valueAnnual = 0,
  onAnnualChange,
  min = 0,
  step = 1,
}) {
  const [period, setPeriod] = useState('annual')
  const [displayValue, setDisplayValue] = useState(valueAnnual || 0)

  useEffect(() => {
    // Keep display synced when parent value changes
    if (period === 'annual') setDisplayValue(to2(valueAnnual || 0))
    else setDisplayValue(to2((valueAnnual || 0) / 12))
  }, [valueAnnual])

  const previewText = useMemo(() => {
    const value = Number(displayValue) || 0
    if (value <= 0) return ''
    if (period === 'monthly') {
      const annual = to2(value * 12)
      return `≈ £${annual.toLocaleString(undefined, { maximumFractionDigits: 2 })} annually`
    }
    const monthly = to2(value / 12)
    return `≈ £${monthly.toLocaleString(undefined, { maximumFractionDigits: 2 })} per month`
  }, [displayValue, period])

  const handleValueChange = (val) => {
    // Avoid forcing 0 while user is typing/clearing
    if (val === '' || val === null || val === undefined) {
      setDisplayValue('')
      onAnnualChange?.(name, 0)
      return
    }
    const rounded = to2(val)
    setDisplayValue(rounded)
    const numeric = Number(rounded) || 0
    const annualised = period === 'annual' ? numeric : to2(numeric * 12)
    onAnnualChange?.(name, annualised)
  }

  const handlePeriodChange = (nextPeriod) => {
    const numeric = Number(displayValue) || 0
    const asAnnual = period === 'annual' ? numeric : to2(numeric * 12)
    const nextDisplay = nextPeriod === 'annual' ? asAnnual : to2(asAnnual / 12)
    setPeriod(nextPeriod)
    setDisplayValue(nextDisplay)
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-800">{label}</label>
        {tooltip ? (
          <Tooltip title={tooltip}>
            <span className="text-xs text-gray-500 cursor-help">What is this?</span>
          </Tooltip>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <InputNumber
          value={displayValue}
          onChange={handleValueChange}
          min={min}
          step={step}
          className="flex-1 h-10"
          stringMode
          precision={2}
          formatter={(value) => `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => value.replace(/£\s?|(,*)/g, '')}
        />
        <Select
          value={period}
          onChange={handlePeriodChange}
          options={periodOptions}
          className="w-28"
        />
      </div>

      <div className="text-xs text-gray-500 min-h-[1rem]">{previewText}</div>
    </div>
  )
}
